<template>
  <div>
    <v-progress-linear
      indeterminate
      color="primary"
      class="my-4"
      v-if="isFetching"
    ></v-progress-linear>
  
    <div style="text-align: end;">
      <v-btn
        variant="flat"
        color="primary"
        size="large"
        @click="() => isCreateDialogOpen = true"
        style="width:300px; margin-top: 16px; margin-bottom: 16px;"
      >
      <v-icon start>mdi-plus</v-icon>
        {{ $t('workScheduling.workDescriptionSettings.create') }}
      </v-btn>
    </div>
  
    <v-table class="mb-8">
      <template v-slot:default>
        <thead>
          <tr>
            <th class="text-left">{{ $t('workScheduling.workDescriptionSettings.tableHeaders.name') }}</th>
            <th class="text-left">{{ $t('workScheduling.workDescriptionSettings.tableHeaders.abbreviation') }}</th>
            <th class="text-left">{{ $t('workScheduling.workDescriptionSettings.tableHeaders.childcareTarget') }}</th>
            <th class="text-left">{{ $t('workScheduling.workDescriptionSettings.tableHeaders.color') }}</th>
            <th class="text-left"></th>
          </tr>
        </thead>
        
        <draggable
          v-model="workDescriptions"
          tag="tbody"
          item-key="workDescriptionId"
          handle=".drag-handle"
        >
          <template #item="{ element: work }">
            <tr>
              <td>
                <v-icon class="drag-handle" size="small"> mdi-drag </v-icon>
                {{ work.name }}
              </td>
              <td>
                {{ work.abbreviation }}
              </td>
              <td class="pl-4">
                {{ work.isChildcare ? $t('common.yes') : '' }}
              </td>
              <td>
                <div class="color-chip" :style="`background-color: ${work.color};`"></div>
              </td>
              <td>
                <v-row>
                  <v-btn
                    class="mx-2"
                    color="primary"
                    variant="outlined"
                    @click="() => editingWork = work"
                  >
                    {{ $t('common.edit') }}
                  </v-btn>
                  <v-btn
                    class="mx-2"
                    color="red"
                    variant="flat"
                    @click="() => deletingWork = work"
                  >
                    {{ $t('common.delete') }}
                  </v-btn>
                </v-row>
              </td>
            </tr>
          </template>
        </draggable>
      </template>
    </v-table>
  
    <v-dialog max-width="720px" v-model="isCreateDialogOpen">
      <v-card>
        <v-card-title>{{ $t('workScheduling.workDescriptionSettings.createTitle') }}</v-card-title>
        <v-card-text>
          <v-text-field
            class="mb-4"
            hide-details
            variant="outlined"
            :label="$t('workScheduling.workDescriptionSettings.form.name')"
            v-model="newWorkTitle"
          />
          <v-text-field
            class="mb-4"
            hide-details
            variant="outlined"
            :label="$t('workScheduling.workDescriptionSettings.form.abbreviation')"
            v-model="newWorkShorten"
          />
          <div>{{ $t('workScheduling.workDescriptionSettings.tableHeaders.color') }}</div>
          <div class="mb-4">
            <color-picker v-model="newWorkColor" />
          </div>
          <div class="mb-4">
            <v-checkbox
              v-model="newWorkIsChildcare"
              :label="$t('workScheduling.workDescriptionSettings.form.childcareTarget')"
              hide-details
              density="compact"
            ></v-checkbox>
          </div>
        </v-card-text>
        <v-card-actions class="justify-center">
          <v-btn
            variant="flat"
            color="primary"
            :disabled="!isNewPatternValid"
            @click="createWorkDescription"
          >
            {{ $t('common.save') }}
          </v-btn>
        </v-card-actions>
        <v-progress-linear
          v-if="isCreatingNewWork"
          indeterminate
          color="primary"
        ></v-progress-linear>
      </v-card>
    </v-dialog>
  
    <v-dialog max-width="720px" v-model="isEditDialogOpen">
      <v-card v-if="editingWork">
        <v-card-title>{{ $t('workScheduling.workDescriptionSettings.editTitle') }}</v-card-title>
        <v-card-text>
          <v-text-field
            class="mb-4"
            hide-details
            variant="outlined"
            :label="$t('workScheduling.workDescriptionSettings.form.name')"
            v-model="editingWork.name"
          />
          <v-text-field
            class="mb-4"
            hide-details
            variant="outlined"
            :label="$t('workScheduling.workDescriptionSettings.form.abbreviation')"
            v-model="editingWork.abbreviation"
          />
          <div>{{ $t('workScheduling.workDescriptionSettings.tableHeaders.color') }}</div>
          <div class="mb-4">
            <color-picker v-model="editingWork.color" />
          </div>
          <div class="mb-4">
            <v-checkbox
              v-model="editingWork.isChildcare"
              :label="$t('workScheduling.workDescriptionSettings.form.childcareTarget')"
              hide-details
              density="compact"
            ></v-checkbox>
          </div>
        </v-card-text>
        <v-card-actions class="justify-center">
          <v-btn variant="flat" color="primary" @click="updateWork">
            {{ $t('common.save') }}
          </v-btn>
        </v-card-actions>
        <v-progress-linear
          v-if="isUpdatingWork"
          indeterminate
          color="primary"
        ></v-progress-linear>
      </v-card>
    </v-dialog>
  
    <v-dialog max-width="720px" v-model="isDeleteDialogOpen">
      <v-card v-if="deletingWork">
        <v-card-title>{{ $t('common.delete') }}</v-card-title>
        <v-card-text>
          {{ $t('workScheduling.workDescriptionSettings.deleteConfirm', { name: deletingWork.name }) }}
        </v-card-text>
        <v-card-actions class="justify-center">
          <v-btn
            variant="flat"
            color="grey"
            class="mx-2"
            @click="() => deletingWork = null"
          >
            {{ $t('common.cancel') }}
          </v-btn>
          <v-btn variant="flat" color="red" class="mx-2" @click="deleteWork">
            {{ $t('common.delete') }}
          </v-btn>
        </v-card-actions>
        <v-progress-linear
          v-if="isDeletingWork"
          indeterminate
          color="primary"
        ></v-progress-linear>
      </v-card>
    </v-dialog>
  </div>
  </template>
  
  <script lang="ts">
  import { defineComponent } from 'vue';
  import draggable from "vuedraggable";
  import ColorPicker from '@/components/molecules/ColorPicker.vue';
  import {
    workDescriptionModule
  } from '@/store/viewModules/work-schedule/workDescriptionModule';
  import { WorkDescriptionSettingResponse } from 'chaild-api/lib';
  
  interface DataType {
    isFetching: boolean;
    isCreateDialogOpen: boolean;
    newWorkTitle: string;
    newWorkShorten: string;
    newWorkColor: string;
    newWorkIsChildcare: boolean;
    isCreatingNewWork: boolean;
    isUpdatingWork: boolean;
    editingWork: WorkDescriptionSettingResponse | null;
    isDeletingWork: boolean;
    deletingWork: WorkDescriptionSettingResponse | null;
  }
  
  export default defineComponent({
    components: {
      ColorPicker,
      draggable,
    },
    data: (): DataType => {
      return {
        isFetching: false,
        isCreateDialogOpen: false,
        newWorkTitle: '',
        newWorkShorten: '',
        newWorkColor: '#029ec0',
        newWorkIsChildcare: false,
        isCreatingNewWork: false,
  
        isUpdatingWork: false,
        editingWork: null,
  
        isDeletingWork: false,
        deletingWork: null,
      };
    },
    computed: {
      isNewPatternValid() {
        return (
          this.newWorkTitle.length > 0 &&
          this.newWorkShorten.length > 0
        )
      },
      workDescriptions: {
        get(): WorkDescriptionSettingResponse[] {
          // ⭕️ 修正5: Storeの配列を直接vuedraggableにいじらせないようにクローンする
          return [...workDescriptionModule.workDescriptions];
        },
        async set(value: WorkDescriptionSettingResponse[]) {
          if(value) {
            const WorkDescriptionsIds = value.map((item) => item)
            workDescriptionModule.setWorkDescriptions(WorkDescriptionsIds)
            
            this.isFetching = true
            await workDescriptionModule.sortWorkDescriptions()
            
            await workDescriptionModule.listWorkDescriptions()
            this.isFetching = false
          }
        }
      },
      isEditDialogOpen: {
        get() {
          return this.editingWork !== null
        },
        set(value: boolean) {
          if (!value) {
            this.editingWork = null
          }
        }
      },
      isDeleteDialogOpen: {
        get() {
          return this.deletingWork !== null
        },
        set(value: boolean) {
          if (!value) {
            this.deletingWork = null
          }
        }
      }
    },
    methods: {
      async createWorkDescription() {
        if (this.isNewPatternValid) {
          this.isCreatingNewWork = true
          await workDescriptionModule.createWorkDescription({
            name: this.newWorkTitle,
            abbreviation: this.newWorkShorten,
            color: this.newWorkColor,
            isChildcare: this.newWorkIsChildcare
          })
          this.isCreatingNewWork = false
  
          this.isFetching = true
          await workDescriptionModule.sortWorkDescriptions()
          await workDescriptionModule.listWorkDescriptions()
          this.isFetching = false
  
          this.isCreateDialogOpen = false
        }
      },
      async updateWork() {
        if (this.editingWork) {
          this.isUpdatingWork = true
          await workDescriptionModule.updateWorkDescription({
            workDescriptionId: this.editingWork.workDescriptionId,
            name: this.editingWork.name,
            abbreviation: this.editingWork.abbreviation,
            color: this.editingWork.color,
            isChildcare: this.editingWork.isChildcare
          })
          this.isUpdatingWork = false
          this.editingWork = null
        }
      },
      async deleteWork() {
        if (this.deletingWork) {
          this.isDeletingWork = true
          await workDescriptionModule.deleteWorkDescription(this.deletingWork.workDescriptionId)
          this.isDeletingWork = false
          
          this.isFetching = true
          await workDescriptionModule.sortWorkDescriptions()
          await workDescriptionModule.listWorkDescriptions()
          this.isFetching = false
          
          this.deletingWork = null
        }
      }
    },
    async mounted() {
      this.isFetching = true
      await workDescriptionModule.listWorkDescriptions()
      this.isFetching = false
    }
  })
  </script>
  <style lang="scss" scoped>
  .color-chip {
    height: 30px;
    width: 90px;
    border-radius: 4px;
    border: 1px solid #ccc; /* 明るい色でも見えるように */
  }
  
  .drag-handle {
    margin-bottom: 2px;
    vertical-align: middle;
    cursor: grab;
  }
  
  .drag-handle:active {
    cursor: grabbing;
  }
  </style>